import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Box, Typography, TextField, IconButton, Stack, Paper, Chip,
  CircularProgress, Checkbox, FormControlLabel, Select, MenuItem, FormControl,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AddIcon from '@mui/icons-material/Add';
import { authFetch, authFetchRequest } from '../../../utils/api';

const COLOR_PRESETS = [
  '#1976d2', '#ed6c02', '#2e7d32', '#9c27b0', '#d32f2f',
  '#0288d1', '#f57c00', '#388e3c', '#7b1fa2', '#c62828',
  '#00796b', '#455a64', '#5d4037',
];

// Maps position order to a default color
const POSITION_COLORS = [
  '#1976d2', '#0288d1', '#ed6c02', '#f57c00', '#2e7d32',
  '#388e3c', '#9c27b0', '#7b1fa2', '#00796b', '#455a64', '#d32f2f',
];

/** Convert DB row → internal format used by Pipeline */
function dbToLocal(s) {
  return {
    id:                    s.id,
    key:                   s.status,
    label:                 s.status,
    color:                 POSITION_COLORS[(s.position - 1) % POSITION_COLORS.length] || '#1976d2',
    position:              s.position,
    active:                s.active !== false,
    requires_approval:     !!s.requires_approval,
    sales_rep_can_advance: s.sales_rep_can_advance !== false,
    rejection_stage_id:    s.rejection_stage_id || null,
  };
}

/** Fetch all active statuses from the DB and return as local format */
export async function loadStatusesFromDB() {
  try {
    const data = await authFetch('/order-statuses/');
    if (Array.isArray(data)) return data.map(dbToLocal);
  } catch (e) { /* fall through */ }
  return [];
}

const StatusManager = ({ open, onClose, onSave }) => {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading]   = useState(true);

  const [newName, setNewName]   = useState('');
  const [newColor, setNewColor] = useState('#1976d2');
  const [error, setError]       = useState('');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    authFetch('/order-statuses/?all=true')
      .then(data => setStatuses((data || []).map(dbToLocal)))
      .catch(() => setStatuses([]))
      .finally(() => setLoading(false));
  }, [open]);

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name) { setError('Status name is required'); return; }
    if (statuses.some(s => s.key.toLowerCase() === name.toLowerCase())) {
      setError('Status already exists');
      return;
    }
    try {
      const maxPos = statuses.reduce((m, s) => Math.max(m, s.position), 0);
      const created = await authFetchRequest('/order-statuses/', { status: name, position: maxPos + 1 }, 'POST');
      setStatuses(prev => [...prev, dbToLocal(created)]);
      setNewName('');
      setNewColor('#1976d2');
      setError('');
    } catch (e) {
      setError('Failed to add status');
    }
  };

  const handleDelete = async (s) => {
    try {
      await authFetchRequest(`/order-statuses/${s.id}`, null, 'DELETE');
      setStatuses(prev => prev.filter(x => x.id !== s.id));
    } catch (e) {
      setError('Failed to delete status');
    }
  };

  const handleMove = async (index, direction) => {
    const arr = [...statuses];
    const target = index + direction;
    if (target < 0 || target >= arr.length) return;
    [arr[index], arr[target]] = [arr[target], arr[index]];
    const reordered = arr.map((s, i) => ({ ...s, position: i + 1 }));
    setStatuses(reordered);
    try {
      await authFetchRequest('/order-statuses/reorder',
        reordered.map(s => ({ id: s.id, position: s.position })),
        'PUT');
    } catch (e) { /* non-critical */ }
  };

  const handleColorChange = (id, color) => {
    setStatuses(prev => prev.map(s => s.id === id ? { ...s, color } : s));
  };

  const handleFieldChange = (id, field, value) => {
    setStatuses(prev => prev.map(s => s.id === id ? { ...s, [field]: value } : s));
  };

  const handleSave = async () => {
    if (statuses.length === 0) { setError('At least one status is required'); return; }
    // Persist approval/advance settings for each pipeline status
    try {
      const pipeline = statuses.filter(s => s.position > 0);
      await Promise.all(pipeline.map(s =>
        authFetchRequest(`/order-statuses/${s.id}`, {
          active:                s.active,
          requires_approval:     s.requires_approval,
          sales_rep_can_advance: s.sales_rep_can_advance,
          rejection_stage_id:    s.rejection_stage_id || null,
        }, 'PUT')
      ));
    } catch (e) {
      setError('Failed to save settings');
      return;
    }
    onSave(statuses);
    onClose();
  };

  // Statuses shown in pipeline (exclude position -1 Cancelled)
  const pipelineStatuses = statuses.filter(s => s.position > 0);
  const cancelledStatus  = statuses.find(s => s.position === -1);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Manage Pipeline Statuses</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
          Configure each stage: color, order, approval requirements, and rep permissions.
        </Typography>

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={28} /></Box>
        ) : (
          <>
            <Stack spacing={1} sx={{ mb: 2 }}>
              {pipelineStatuses.map((s, index) => (
                <Paper key={s.id} variant="outlined" sx={{ p: 1.5, borderLeft: `4px solid ${s.color}`, opacity: s.active ? 1 : 0.5 }}>
                  {/* Row 1: name, color, reorder, delete */}
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                    <Typography variant="body2" sx={{ flex: 1, fontWeight: 'bold', fontSize: '13px' }}>
                      {s.key}
                    </Typography>

                    {/* Color picker */}
                    <Stack direction="row" spacing={0.3} flexWrap="wrap" sx={{ maxWidth: 120 }}>
                      {COLOR_PRESETS.slice(0, 6).map(c => (
                        <Box
                          key={c}
                          onClick={() => handleColorChange(s.id, c)}
                          sx={{
                            width: 16, height: 16, borderRadius: '50%', bgcolor: c, cursor: 'pointer',
                            border: s.color === c ? '2px solid #333' : '1px solid #ddd',
                          }}
                        />
                      ))}
                    </Stack>

                    <IconButton size="small" onClick={() => handleMove(index, -1)} disabled={index === 0}>
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleMove(index, 1)} disabled={index === pipelineStatuses.length - 1}>
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(s)} sx={{ color: '#d32f2f' }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>

                  {/* Row 2: approval settings */}
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ pl: 0.5, flexWrap: 'wrap' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={s.active}
                          onChange={e => handleFieldChange(s.id, 'active', e.target.checked)}
                        />
                      }
                      label={<Typography variant="caption" sx={{ color: s.active ? 'inherit' : '#d32f2f' }}>Active</Typography>}
                      sx={{ m: 0 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={s.requires_approval}
                          onChange={e => handleFieldChange(s.id, 'requires_approval', e.target.checked)}
                        />
                      }
                      label={<Typography variant="caption">Requires approval</Typography>}
                      sx={{ m: 0 }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={s.sales_rep_can_advance}
                          onChange={e => handleFieldChange(s.id, 'sales_rep_can_advance', e.target.checked)}
                        />
                      }
                      label={<Typography variant="caption">Sales rep can advance</Typography>}
                      sx={{ m: 0 }}
                    />
                    {s.requires_approval && (
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant="caption" sx={{ color: '#666', whiteSpace: 'nowrap' }}>
                          On rejection →
                        </Typography>
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                          <Select
                            value={s.rejection_stage_id || ''}
                            onChange={e => handleFieldChange(s.id, 'rejection_stage_id', e.target.value || null)}
                            displayEmpty
                            sx={{ fontSize: '12px', height: 28 }}
                          >
                            <MenuItem value=""><em>Stay (note only)</em></MenuItem>
                            {pipelineStatuses
                              .filter(other => other.id !== s.id)
                              .map(other => (
                                <MenuItem key={other.id} value={other.id} sx={{ fontSize: '12px' }}>
                                  {other.key}
                                </MenuItem>
                              ))
                            }
                          </Select>
                        </FormControl>
                      </Stack>
                    )}
                  </Stack>
                </Paper>
              ))}
            </Stack>

            {/* Cancelled shown separately */}
            {cancelledStatus && (
              <Typography variant="caption" sx={{ color: '#888' }}>
                "Cancelled" is always available as a terminal status (position −1).
              </Typography>
            )}

            {/* Add new */}
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#f9f9f9', mt: 2 }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold', mb: 1, display: 'block' }}>
                Add New Status
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  size="small"
                  label="Status Name"
                  value={newName}
                  onChange={(e) => { setNewName(e.target.value); setError(''); }}
                  placeholder="e.g. Measuring"
                  sx={{ flex: 1 }}
                />
                <input
                  type="color"
                  value={newColor}
                  onChange={(e) => setNewColor(e.target.value)}
                  style={{ width: 32, height: 32, border: 'none', cursor: 'pointer', padding: 0 }}
                />
                <IconButton onClick={handleAdd} color="primary">
                  <AddIcon />
                </IconButton>
              </Stack>
              {error && (
                <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, display: 'block' }}>
                  {error}
                </Typography>
              )}
            </Paper>

            {/* Preview */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" sx={{ color: '#888', mb: 0.5, display: 'block' }}>Preview:</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {pipelineStatuses.map((s) => (
                  <Chip key={s.id} label={s.key} size="small"
                    sx={{ bgcolor: s.color, color: '#fff', fontWeight: 'bold' }} />
                ))}
              </Stack>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={loading}>
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StatusManager;
